import React, { useState, useEffect, useCallback } from 'react';
import { calendarApi } from '../lib/api';
import { formatCurrency, DEAL_STAGES } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  CaretLeft, CaretRight, ClipboardText, Handshake, ShoppingCart, CalendarBlank
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const TYPE_CONFIGS = {
  task: { icon: ClipboardText, label: 'Task', badgeClass: 'bg-blue-500 text-white' },
  deal: { icon: Handshake, label: 'Deal', badgeClass: 'bg-amber-500 text-white' },
  procurement_start: { icon: ShoppingCart, label: 'Procurement Start', badgeClass: 'bg-purple-500 text-white' },
  procurement_deadline: { icon: ShoppingCart, label: 'Deadline', badgeClass: 'bg-red-500 text-white' },
};

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [viewMode, setViewMode] = useState('month');

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const fetchEvents = useCallback(async () => {
    try {
      const startDate = new Date(year, month, 1).toISOString().split('T')[0];
      const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];
      const response = await calendarApi.getEvents({ start: startDate, end: endDate });
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to load events');
    } finally { setLoading(false); }
  }, [year, month]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday = () => setCurrentDate(new Date());

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();

  const getEventsForDate = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.date === dateStr && (typeFilter === 'all' || e.type === typeFilter));
  };

  const filteredEvents = typeFilter === 'all' ? events : events.filter(e => e.type === typeFilter);
  const selectedDateEvents = selectedDate ? getEventsForDate(selectedDate) : [];
  const today = new Date();
  const isToday = (day) => today.getDate() === day && today.getMonth() === month && today.getFullYear() === year;

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Calendar</h1>
          <p className="text-muted-foreground text-sm mt-1">Tasks, deals & procurement deadlines</p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-44" data-testid="type-filter">
              <SelectValue placeholder="All Events" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              <SelectItem value="task">Tasks</SelectItem>
              <SelectItem value="deal">Deals</SelectItem>
              <SelectItem value="procurement_start">Procurement Start</SelectItem>
              <SelectItem value="procurement_deadline">Deadlines</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Calendar Grid */}
        <div className="flex-1">
          {/* Month Header */}
          <div className="flex items-center justify-between mb-4 bg-card border border-border p-4 rounded-sm">
            <Button variant="ghost" size="sm" onClick={prevMonth} data-testid="prev-month">
              <CaretLeft size={20} />
            </Button>
            <div className="text-center">
              <h2 className="font-heading text-xl font-semibold">{MONTHS[month]} {year}</h2>
              <p className="text-xs text-muted-foreground">{filteredEvents.length} events this month</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={goToday} data-testid="go-today">Today</Button>
              <Button variant="ghost" size="sm" onClick={nextMonth} data-testid="next-month">
                <CaretRight size={20} />
              </Button>
            </div>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 mb-1">
            {DAYS.map(day => (
              <div key={day} className="p-2 text-center text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 border-t border-l border-border">
            {/* Empty cells for days before first day of month */}
            {Array.from({ length: firstDayOfMonth }).map((_, i) => (
              <div key={`empty-${i}`} className="h-28 border-r border-b border-border bg-muted/30"></div>
            ))}

            {/* Day cells */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dayEvents = getEventsForDate(day);
              const isSelected = selectedDate === day;
              return (
                <div
                  key={day}
                  className={`h-28 border-r border-b border-border p-1.5 cursor-pointer transition-colors ${
                    isSelected ? 'bg-primary/5 ring-1 ring-primary' : 'hover:bg-muted/50'
                  } ${isToday(day) ? 'bg-primary/5' : 'bg-card'}`}
                  onClick={() => setSelectedDate(day === selectedDate ? null : day)}
                  data-testid={`day-${day}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-sm font-medium ${isToday(day) ? 'bg-primary text-primary-foreground h-6 w-6 rounded-full flex items-center justify-center' : ''}`}>
                      {day}
                    </span>
                    {dayEvents.length > 0 && (
                      <span className="text-xs text-muted-foreground">{dayEvents.length}</span>
                    )}
                  </div>
                  <div className="space-y-0.5 overflow-hidden">
                    {dayEvents.slice(0, 3).map((evt) => (
                      <div
                        key={evt.id}
                        className="text-xs px-1 py-0.5 rounded-sm truncate"
                        style={{ backgroundColor: evt.color + '20', color: evt.color, borderLeft: `2px solid ${evt.color}` }}
                      >
                        {evt.title}
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <p className="text-xs text-muted-foreground px-1">+{dayEvents.length - 3} more</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sidebar - Selected Date Events */}
        <div className="w-80 flex-shrink-0">
          <div className="bg-card border border-border rounded-sm sticky top-6">
            <div className="p-4 border-b border-border">
              <h3 className="font-heading font-semibold">
                {selectedDate
                  ? `${MONTHS[month]} ${selectedDate}, ${year}`
                  : 'Upcoming Events'
                }
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                {selectedDate ? `${selectedDateEvents.length} events` : `${filteredEvents.length} total`}
              </p>
            </div>
            <div className="p-3 space-y-2 max-h-[60vh] overflow-y-auto">
              {(selectedDate ? selectedDateEvents : filteredEvents.slice(0, 15)).map((evt) => {
                const config = TYPE_CONFIGS[evt.type] || TYPE_CONFIGS.task;
                const Icon = config.icon;
                return (
                  <div key={evt.id} className="p-3 bg-muted/50 rounded-sm animate-fade-in" data-testid={`event-${evt.id}`}>
                    <div className="flex items-start gap-2">
                      <div className="h-7 w-7 rounded-sm flex items-center justify-center flex-shrink-0" style={{ backgroundColor: evt.color + '20' }}>
                        <Icon size={14} style={{ color: evt.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{evt.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={`${config.badgeClass} text-xs`}>{config.label}</Badge>
                          {evt.value > 0 && <span className="text-xs text-muted-foreground">{formatCurrency(evt.value)}</span>}
                          {evt.priority && <span className="text-xs text-muted-foreground capitalize">{evt.priority}</span>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{evt.date}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
              {(selectedDate ? selectedDateEvents : filteredEvents).length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <CalendarBlank size={32} className="mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No events</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
